/**
    * @description      : Payment Modal component for processing invoice payments
    * @author           : Saif
    * @group            : 
    * @created          : 04/11/2024 - 22:30:43
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 04/11/2024
    * - Author          : Saif
    * - Modification    : 
**/
import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import CustomModal from '../CustomModal';
import CustomButton from '../CustomButton';
import './style.css';

const PaymentModal = ({ show, close, invoice }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [resultType, setResultType] = useState(''); // 'success', 'error', 'info'
  
  const stripe = useStripe();
  const elements = useElements();

  // Debug Stripe initialization
  console.log('Stripe initialized:', !!stripe);
  console.log('Elements initialized:', !!elements);
  console.log('Stripe publishable key:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  

  const handlePayment = async () => {
    if (!stripe || !elements || !invoice) return;

    setLoading(true);
    setResult('');
    setResultType('');

    try {
      // 1. Create payment intent from backend
      console.log('Creating payment intent for invoice:', invoice.invoice_id);
      
      // Make the API call directly using fetch
      const token = localStorage.getItem('login');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://kvr.kayifi.com/api'}/paynow/pay-with-stripe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invoice_id: invoice.invoice_id
        }),
      });

      const paymentData = await response.json();
      console.log('Payment intent response:', paymentData);

      // Handle different response formats
      const clientSecret = paymentData?.clientSecret || 
                          paymentData?.data?.clientSecret || 
                          paymentData?.payment_intent?.client_secret;

      // The backend only creates a PaymentIntent; actual payment happens after Stripe confirmation.
      if (paymentData && paymentData.status === 'success' && paymentData.payment_status === 'succeeded') {
        setResult('Payment successful! Your invoice has been paid.');
        setResultType('success');

        // Close modal after 2 seconds on success
        setTimeout(() => {
          close();
          // Refresh the page or update the invoice list
          window.location.reload();
        }, 2000);
      } else if (paymentData && clientSecret) {
        // 2. Confirm card payment with Stripe.js
        const cardElement = elements.getElement(CardElement);
        const resultStripe = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
          },
        });

        if (resultStripe.error) {
          setResult('Payment failed: ' + resultStripe.error.message);
          setResultType('error');
        } else if (resultStripe.paymentIntent && resultStripe.paymentIntent.status === 'succeeded') {
          // 3. Notify backend so invoice/booking/wallet/earnings are updated only after real payment.
          const confirmResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://kvr.kayifi.com/api'}/paynow/confirm-payment`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              payment_intent_id: resultStripe.paymentIntent.id,
            }),
          });

          const confirmData = await confirmResponse.json().catch(() => null);

          if (confirmResponse.ok && confirmData?.success) {
            setResult('Payment successful! Your invoice has been paid.');
            setResultType('success');

            // Close modal after 2 seconds on success
            setTimeout(() => {
              close();
              // Refresh the page or update the invoice list
              window.location.reload();
            }, 2000);
          } else {
            setResult('Payment was charged but we could not update the invoice. Please contact support.');
            setResultType('error');
          }
        } else {
          console.log('Stripe payment result:', resultStripe);
          setResult('Payment failed or incomplete. Status: ' + (resultStripe.paymentIntent?.status || 'unknown'));
          setResultType('error');
        }
      } else {
        console.error('Payment intent creation failed:', paymentData);
        const errorMessage = paymentData?.message || paymentData?.error || 'Failed to create payment intent. Please try again.';
        setResult('Error: ' + errorMessage);
        setResultType('error');
      }
    } catch (err) {
      setResult('Payment error: ' + (err.message || 'Something went wrong. Please try again.'));
      setResultType('error');
    }

    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getResultIcon = () => {
    switch (resultType) {
      case 'success':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-success" />;
      case 'error':
        return <FontAwesomeIcon icon={faExclamationCircle} className="text-danger" />;
      default:
        return <FontAwesomeIcon icon={faLock} className="text-primary" />;
    }
  };

  const getResultClass = () => {
    switch (resultType) {
      case 'success':
        return 'alert-success';
      case 'error':
        return 'alert-danger';
      default:
        return 'alert-info';
    }
  };

  return (
    <CustomModal 
      show={show} 
      close={close} 
      heading="Pay Invoice"
      size="md"
    >
      <div className="payment-modal-content">
        {invoice && (
          <>
            {/* Invoice Summary */}
            <div className="invoice-summary mb-4">
              <div className="summary-row">
                <span className="summary-label">Invoice #:</span>
                <span className="summary-value">{invoice.invoice_id}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Description:</span>
                <span className="summary-value">{invoice.billing_for}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Amount:</span>
                <span className="summary-value amount">{formatCurrency(invoice.total)}</span>
              </div>
            </div>

            {/* Stripe Card Element */}
            <div className="card-element-container mb-4">
              <label className="form-label">
                <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                Card Information
              </label>
              <div className="card-element-wrapper">
                {!stripe || !elements ? (
                  <div className="alert alert-warning">
                    <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                    Stripe is not properly initialized. Please check your Stripe configuration.
                  </div>
                ) : (
                  <CardElement 
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                    }}
                  />
                )}
              </div>
            </div>

            {/* Payment Result */}
            {result && (
              <div className={`alert ${getResultClass()} d-flex align-items-center mb-4`}>
                {getResultIcon()}
                <span className="ms-2">{result}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="payment-actions d-flex gap-3 justify-content-end">
              <CustomButton
                text="Cancel"
                variant="outline-secondary"
                onClick={close}
                disabled={loading}
              />
              <CustomButton
                text={loading ? 'Processing...' : `Pay ${formatCurrency(invoice.total)}`}
                variant="primary"
                onClick={handlePayment}
                disabled={loading || !stripe || !elements}
                icon={loading ? faSpinner : faCreditCard}
              />
            </div>
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default PaymentModal;
